/* eslint-disable @nx/enforce-module-boundaries */
import {
  MultiChoiceQuestionConfig,
  MultipleChoiceComposedElement,
} from '@hyper-inferno/multiple-choice-core';
import {
  QuestionHandler,
  ITransformationContext,
} from '@hyper-inferno/survey-bot-core';
import {
  BaseComposedElement,
  QuestionAnswerObject,
  QuestionType,
  SurveyElement,
} from '@hyper-inferno/survey-elements-core';

export class MultipleChoiceHandler
  implements QuestionHandler<MultiChoiceQuestionConfig>
{
  async buildConfig(el: any, ctx: ITransformationContext) {
    const options = await ctx.listBuilding.getOptionsByListId(
      el.config.list_id,
      ctx,
      el.config.randomize_options,
    );

    return {
      ...el.config,
      options,
    };
  }

  getValue(el: SurveyElement, response: QuestionAnswerObject) {
    // Regex: Starts with el.name, followed by _, ends with one or more digits
    const pattern = new RegExp(`^${el.name}_(\\d+)$`);

    return Object.keys(response)
      .filter((key) => pattern.test(key) && response[key] === 1) // Added value check to get [2, 3]
      .map((key) => {
        const match = key.match(pattern);
        // Now match[1] will contain the digit suffix
        return match ? parseInt(match[1], 10) : null;
      })
      .filter((suffix): suffix is number => suffix !== null);
  }

  /**
   * A multiple choice with one selectable option is a forced answer, and with
   * no selectable option it can only be answered empty. Everything else needs
   * the respondent.
   */

  handleAutoCode(
    el: SurveyElement,
    ctx: ITransformationContext,
    composedElement: BaseComposedElement,
  ): QuestionAnswerObject | null {
    const config = (composedElement as MultipleChoiceComposedElement)[
      QuestionType.MULTIPLE_CHOICE
    ] as MultiChoiceQuestionConfig | undefined;

    if (!config) return null;

    const options = config.options ?? [];

    // Exclude the AFM placeholder itself.
    // Do not reject the question just because an AFM trigger exists.
    const selectableOptions = options.filter(
      (option) => !option.is_afm_trigger && !option.hidden && !option.disabled,
    );

    // No available answer options.
    if (selectableOptions.length === 0) {
      return {};
    }

    // Exactly one selectable option can be auto-coded.
    if (selectableOptions.length === 1) {
      const onlyOption = selectableOptions[0];

      // Open-ended options require respondent input.
      if (onlyOption.is_open_ended) {
        return null;
      }

      return {
        [`${el.name}_${onlyOption.option_key}`]: 1,
      };
    }

    // Multiple selectable options require respondent input.
    return null;
  }
}
